import { differenceInMinutes } from 'date-fns';

import { FsAxonDevice } from '@lib/api/entities';
import { SnCurrentOrganization } from '@lib/auth';
import { FsAdvancedFeatureMarker, IAdvancedStyleOptions } from '@lib/geo/google/map';
import { COLORS, THEME } from '@lib/ui/theme';
import icons from 'libs/ui/icon/src/lib/components/fusus-icons/service/icons.json';
import { AxonNotificationIconService } from '../../services/axon-notification-icon.service';
import { STATE_BASED_NOTIFICATION_CATEGORIES } from '../../constants';
import { DEVICE_GLYPHS, OUTPOST_LIGHTPOST_SVG_TEMPLATE } from '../../constants/device-icon-templates';

import { IDeviceMarker } from '../../interfaces';
import { AxonUsherAlertModel } from '../../models/axon-usher-alert.model';
import { AxonDeviceModels } from '../../models/device-model.enum';

export class FsAxonDeviceAdvancedMarker extends FsAdvancedFeatureMarker implements IDeviceMarker {
  public icons: any[];
  public hasHighPriorityHistory = false;
  public selected: boolean = false;

  constructor(
    public device: FsAxonDevice,
    private readonly _currentOrganization?: SnCurrentOrganization,
    _axonNotificationIconService?: AxonNotificationIconService,
  ) {
    super({
      id: device.axonDeviceId,
      position: new google.maps.LatLng(
        device.attributes.location?.latitude || 0,
        device.attributes.location?.longitude || 0,
      ),
      feature: {
        type: 'Feature',
        properties: device,
        geometry: {
          type: 'Point',
          coordinates: [
            device.attributes.location?.longitude || 0,
            device.attributes.location?.latitude || 0,
          ],
        },
      },
      isAxonLayer: true,
    }, _axonNotificationIconService);
    this.content = document.createElement('div');
    this.icons = icons.icons;
    this.setAssignee();

    this.icon = {
      fontSet: 'fs',
      fontIcon: this.getIcon(),
    };
    this.styles = {
      fillColor: this.getStatusColor(),
      fillOpacity: 1,
      strokeOpacity: 1,
      scale: 1.2,
    };
    this.iconLabel = {
      text: this.getTitle(),
      top: 30,
      left: this._currentOrganization?.settings?.unitDetailsBeta ? 0 : -5,
      fontSize: '14px',
    };
    this.setDevice(device);
    this.setIcon();
    if (!this.shouldUseSvgIcon()) {
      this.setStyles(this.content);
    }
    this.setIconLabel();
    this.content.classList.add('fs__advanced-feature-marker');
    this.content.classList.add('fs__center-marker');
    this.content.classList.add('fs__advanced-marker');
    this.getMarker().content = this.content;

    const { firstName, lastName, internalId } = device.attributes?.assignees?.[0] ?? {};

    if (this._currentOrganization?.settings?.unitDetailsBeta && internalId) {
      const content = this.getMarker().content;
      const tooltipElement = document.createElement('div');
      tooltipElement.classList.add('fs__axon-marker-tooltip');

      content.addEventListener('mouseenter', () => {
        tooltipElement.innerHTML = `
          <div>
            ${firstName} ${lastName}
          </div>
        `;

        content.appendChild(tooltipElement);
      });

      content.addEventListener('mouseleave', () => {
        if (content.contains(tooltipElement)) {
          content.removeChild(tooltipElement);
        }
      });
    }
  }

  getIsCameraActive(): boolean {
    const status = this.device.attributes.status;
    return status !== 'INACTIVE' && status !== 'OFFLINE';
  }

  getDeviceStatus(): string {
    return this.device.attributes.status;
  }

  isRecording(): boolean {
    return this.getDeviceStatus() === 'RECORDING';
  }

  isBuffering(): boolean {
    return this.getDeviceStatus() === 'BUFFERING';
  }

  isInactive(): boolean {
    return this.getDeviceStatus() === 'INACTIVE';
  }

  getDeviceModel(): string {
    return this.device.deviceModel;
  }

  getAssignees(): any[] {
    return this.device.attributes?.assignees;
  }

  getDeviceUpdateTimestamp(): number {
    return this.device.attributes.deviceUpdateTimestamp;
  }

  getVehicleName(): string {
    return this.device.attributes?.vehicle?.vehicleName;
  }

  canStream(): boolean {
    return (
      this.device.attributes?.status === 'RECORDING' || this.device.attributes?.stream?.isStreamable
    );
  }

  getStreamReason(): string {
    return this.device.attributes?.stream?.reason;
  }

  get primaryAssignee(): any {
    if (!this.device?.attributes?.assignees?.length) {
      return null;
    }
    return this.device.attributes.assignees[0];
  }

  public setIcon(): void {
    if (this.shouldUseSvgIcon()) {
      this.setSvgIcon();
    } else {
      const paths = this.getPaths(this.icon.fontIcon);
      const icon = document.createElement('span');
      icon.classList.value = `fs ${this.icon.fontIcon}`;
      icon.style.fontSize = this.size;
      paths.forEach((el, i) => {
        const t = document.createElement('span');
        t.classList.value = `path${i + 1}`;
        icon?.appendChild(t);
      });
      icon.classList.add('fs__advanced-feature-icon');
      this.content.appendChild(icon);
    }
  }

  private shouldUseSvgIcon(): boolean {
    const isOutpost = this.device.deviceModel === AxonDeviceModels.AxonOutpost;
    const featureFlagEnabled = this._currentOrganization?.settings?.enableAdvancedDotsOnMapIcon ?? false;
    return isOutpost && featureFlagEnabled;
  }

  private setSvgIcon(): void {
    const glyph = DEVICE_GLYPHS[this.device.deviceModel] || '';
    const statusColor = this.getStatusColor();

    const svgContent = OUTPOST_LIGHTPOST_SVG_TEMPLATE.replace('<!-- GLYPH_CONTENT -->', glyph);

    const container = document.createElement('div');
    container.innerHTML = svgContent;
    container.style.cssText = `--status-color: ${statusColor}`;
    container.classList.add('fs__advanced-feature-icon');

    this.content.appendChild(container);
  }

  public setAssignee(): void {
    const title = this.device.deviceName;
    this.setTitle(title);
  }

  public getIcon(): string {
    switch (this.device.deviceModel) {
      case AxonDeviceModels.AxonBody3:
        return 'ab3';
      case AxonDeviceModels.AxonBody4:
        return 'ab3';
      case AxonDeviceModels.AxonBodyWorkforce:
        return 'ab3';
      case AxonDeviceModels.AxonBodyWorkforceMini:
        return 'ab3';
      case AxonDeviceModels.AxonFleet3Hub:
        return 'car';
      case AxonDeviceModels.AxonFleet3DualViewCamera:
        return 'car';
      case AxonDeviceModels.AxonFleet3InteriorCamera:
        return 'car';
      // TODO: Update icon for Axon Outpost when it is available: https://taserintl.atlassian.net/browse/FWX-2622
      case AxonDeviceModels.AxonOutpost:
        return 'car';
      // TODO: Update icon for Axon Lightpost when icon is available: https://taserintl.atlassian.net/browse/PXL-26
      case AxonDeviceModels.AxonLightpost:
        return 'car';
      default:
        return 'ab3';
    }
  }

  public getPaths(iconName: string): any[] {
    return this.icons.find((icon) => icon.properties.name === iconName).icon.paths;
  }

  public getStatusColor(): string {
    const isOutpostOrLightpost = this.shouldUseSvgIcon();
    switch (this.device.attributes?.status) {
      case 'RECORDING':
        return isOutpostOrLightpost ? THEME['axon-device-online'] : '#00ff00';
      case 'BUFFERING':
        return isOutpostOrLightpost ? THEME['axon-device-online'] : THEME.success;
      case 'INACTIVE':
        return isOutpostOrLightpost ? THEME['axon-device-offline'] : COLORS['grey-500'];
      case 'OFFLINE':
        return isOutpostOrLightpost ? THEME['axon-device-offline'] : COLORS['grey-500'];
      case 'DOCKED':
        return '#F29339';
      default:
        return COLORS['grey-500'];
    }
  }

  public setIconLabel(): void {
    if (this.iconLabel?.text) {
      const iconLabel = document.createElement('span');
      iconLabel.innerText = this.iconLabel.text;
      this.content.appendChild(iconLabel);
      iconLabel.classList.add('fs__advanced-feature-marker-label');
      iconLabel.style.top = `${this.iconLabel.top}px`;
      iconLabel.style.left = `${this.iconLabel.left}px`;
    }
  }

  public getDeviceHistory(): any[] {
    return this.deviceHistory;
  }

  public setFeatures(features: any): void {
    this.feature = features;
    if (this.iconLabel?.text) {
      const existingLabel: HTMLElement = this.content.querySelector(
        '.fs__advanced-feature-marker-label',
      );
      if (existingLabel) {
        existingLabel.innerText = this.iconLabel.text;
        existingLabel.style.top = `${this.iconLabel.top}px`;
        existingLabel.style.left = `${this.iconLabel.left}px`;
      } else {
        const iconLabel = document.createElement('span');
        iconLabel.classList.add('fs__advanced-feature-marker-label');
        iconLabel.innerText = this.iconLabel.text;
        this.content.appendChild(iconLabel);
        iconLabel.style.top = `${this.iconLabel.top}px`;
        iconLabel.style.left = `${this.iconLabel.left}px`;
      }
    }
  }

  public updateStyles(styles?: IAdvancedStyleOptions): void {
    this.styles = {
      fillColor: this.getStatusColor(),
      fillOpacity: 1,
      strokeOpacity: 1,
      scale: 1.2,
      ...styles,
    };
    if (!this.shouldUseSvgIcon()) {
      this.setStyles(this.content);
    } else {
      this.updateSvgIconColor();
    }
  }
  
  private updateSvgIconColor(): void {
    const statusColor = this.getStatusColor();
    if (this.content) {
      this.content.style.setProperty('--status-color', statusColor);
    }
  }

  public checkNotifications(content: any[]): void {
    this.bannerContent = content.filter((c) => {
      const isStateBasedNotification = STATE_BASED_NOTIFICATION_CATEGORIES.includes(
        c.data.notification.category as any
      );
      return (
        this.withinXMinutes(c.data.notification.notificationReceivedTime, 15) ||
        isStateBasedNotification
      );
    });

    // Sort notifications descending by timestamp
    this.bannerContent?.sort(
      (a, b) =>
        b.data.notification.notificationReceivedTime - a.data.notification.notificationReceivedTime,
    );

    if (this.bannerContent.length === 0) {
      this.removeNotification();
    } else if (this.banner) {
      // Update banner content and critical state after filtering notifications
      const uniqueIcons = [...new Set(this.bannerContent.map((content) => content.icon))];
      this.banner.innerHTML = uniqueIcons.join(' ');

      this._updateBannerCriticalState();

      const gmMarker: any = this.getMarker();
      gmMarker.axonNotifications = this.bannerContent.map((content) => content.data);
    }
  }

  public processHistoricalData(historicalData: any): void {
    if (historicalData.attributes?.level === 'Alert') {
      this.hasHighPriorityHistory = true;
    }
    this.addDeviceHistory(historicalData);
  }

  public setDevice(device: FsAxonDevice): void {
    this.device = device;
  }

  public updateNotifications(data: AxonUsherAlertModel, highPriority: boolean = false): void {
    if (highPriority) {
      this.hasHighPriorityHistory = true;
    }
    if (data.notification.category === 'WatchMeCancel') {
      this.deviceHistory.push({
        attributes: {
          category: data.notification.category,
          device: {
            deviceId: data.device.deviceId,
          },
          timestamp: data.notification.timeOccurred,
        },
        id: data.notification.id,
      });
      this.removeNotification();
    } else {
      this.createNotificationBanner(data);
    }
  }

  public withinXMinutes(receivedTime: number, minutesDifference: number): boolean {
    return minutesDifference > differenceInMinutes(new Date(), receivedTime);
  }
}
